import { match, matchPath } from 'react-router'
import { createSelector } from 'reselect'
import {
  ExpenseModel,
  ExpenseReportModel,
  ExpenseReportResponse,
  ExpenseResponse,
} from 'src/models/expenseReport.model'
import {
  ExpenseReportMatchParams,
  expenseReportRouteProps,
  UpdateExpenseMatchParams,
  updateExpenseRouteProps,
} from 'src/navigation/routeProps'
import { RootState } from 'src/redux/createRootReducer'
import { selectExpenseReportModelsFromState } from 'src/redux/selectors/data/expenses.selectors'
import { selectReceiptAttachmentsListModels } from 'src/redux/selectors/data/receiptAttachments.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectLocationFromState,
} from 'src/redux/selectors/router.selectors'
import { ErrorDetailMap } from 'src/utils/error.utils'

export const selectExpenseFromState = (
  state: RootState,
): ExpenseResponse | undefined => state.app.updateExpense.expense

export const selectExpenseModelFromState = createSelector(
  [selectExpenseFromState],
  (expense) => (expense !== undefined ? new ExpenseModel(expense) : undefined),
)

export const selectPatchingExpenseFromState = (state: RootState): boolean =>
  state.app.updateExpense.patchingExpense
export const selectDeletingExpenseFromState = (state: RootState): boolean =>
  state.app.updateExpense.deletingExpense

export const selectReceiptAttachmentIdsFromState = (
  state: RootState,
): string[] => state.app.updateExpense.receiptAttachmentIds

export const selectReceiptAttachmentModelsList = createSelector(
  [selectReceiptAttachmentIdsFromState, selectReceiptAttachmentsListModels],
  (ids, receiptAttachments) => ids.map((id) => receiptAttachments[id]),
)

export const selectUploadingAttachmentsFromState = (
  state: RootState,
): Record<string, string> =>
  state.app.updateExpense.uploadingAttachmentThumbnails

export const selectErrorDetailMapFromState = (
  state: RootState,
): ErrorDetailMap<string> | undefined => state.app.updateExpense.errorDetailMap

export const selectTopLevelError = (state: RootState): string | undefined =>
  state.app.updateExpense.topLevelError

export const selectDeletingExpenseTopLevelError = (
  state: RootState,
): string | undefined => state.app.updateExpense.deletingExpenseTopLevelError

export const selectExpenseLoadedFromState = (state: RootState): boolean =>
  state.app.updateExpense.loaded

export const selectExpenseReportFromState = (
  state: RootState,
): ExpenseReportResponse | undefined => state.app.expenseReport.expenseReport

export const selectExpenseReportModelFromState = createSelector(
  [selectExpenseReportFromState],
  (expenseReport) =>
    expenseReport !== undefined
      ? new ExpenseReportModel(expenseReport)
      : undefined,
)

export const selectExpenseIdFromRoute = createSelector(
  [selectLocationFromState],
  (location) => {
    const mtch = matchPath<UpdateExpenseMatchParams>(
      location.pathname,
      updateExpenseRouteProps,
    ) as match<UpdateExpenseMatchParams>
    return mtch.params.id
  },
)

export const selectExpenseReportIdFromRoute = createSelector(
  [selectCalculatedBackgroundLocation],
  (backgroundLocation) => {
    const mtch = matchPath<ExpenseReportMatchParams>(
      backgroundLocation.pathname,
      expenseReportRouteProps,
    ) as match<ExpenseReportMatchParams>
    return mtch.params.id
  },
)

export const selectExpenseReport = createSelector(
  [selectExpenseReportModelsFromState, selectExpenseReportIdFromRoute],
  (expenseReportDetails, expenseReportId) => {
    return expenseReportDetails[expenseReportId] !== undefined
      ? expenseReportDetails[expenseReportId]
      : undefined
  },
)
